import React from 'react';

/**
 * ContextErrorBoundary - wraps context providers to catch their initialization errors
 * If a context provider throws during render, this will catch it and display a fallback
 */
class ContextErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[ContextErrorBoundary]', error.message, errorInfo);

    // ── Chunk-load auto-recovery ───────────────────────────────────────────
    // A stale SW can serve old index.html after a deploy, causing lazy chunks
    // to 404 (Cloudflare returns index.html → wrong MIME → module load fails).
    // React catches the render-phase rejection here before it becomes an
    // unhandledrejection, so we must recover here rather than in index.jsx.
    // Clear all SW caches then reload — NetworkFirst navigation in sw.js will
    // fetch fresh HTML with current chunk hashes on the next load.
    if (/Failed to fetch dynamically imported module|Importing a module script failed/i.test(error?.message ?? '')) {
      console.warn('[ContextErrorBoundary] Stale chunk — unregistering SW, clearing caches, reloading.');
      (async () => {
        try {
          // Unregister the SW first so the reload bypasses it entirely and
          // fetches fresh HTML + chunks straight from Cloudflare. Without this,
          // the still-active SW re-precaches stale entries on reload and the
          // error → cache-clear → reload loop repeats indefinitely.
          if ('serviceWorker' in navigator) {
            const regs = await navigator.serviceWorker.getRegistrations();
            await Promise.all(regs.map((r) => r.unregister()));
          }
          if ('caches' in window) {
            const keys = await caches.keys();
            await Promise.all(keys.map((k) => caches.delete(k)));
          }
        } finally {
          window.location.reload();
        }
      })();
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          background: 'linear-gradient(160deg, #14532d 0%, #1c1917 60%, #292524 100%)',
          padding: '2rem',
          color: '#fff',
        }}>
          <p style={{ textAlign: 'center', maxWidth: '400px', lineHeight: '1.6' }}>
            Failed to initialize: {this.state.error?.message}
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              marginTop: '1rem',
              padding: '0.5rem 1rem',
              background: '#166534',
              color: '#fff',
              border: 'none',
              borderRadius: '0.5rem',
              cursor: 'pointer',
              fontWeight: '600',
            }}
          >
            Reload App
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ContextErrorBoundary;